import { isPassingTrain, TrackOccupation } from "@opstel/query/models/trackOccupation";
import { getTrackOccupationPosition } from "@opstel/SBD/core/functions";
import * as Toggle from "@radix-ui/react-toggle";
import { useSelectedTimeStore } from "@shared/common/contexts/SelectedTime/useSelectedTimeStore";
import { ContextMenu } from "@shared/EDS/components/ContextMenu";
import { useTheme } from "@shared/EDS/contexts/useTheme";
import { DateTime } from "luxon";
import React, { memo, useCallback } from "react";
import {
  SBD_ACTION_ICON_GAP,
  SBD_ACTION_ICON_PADDING,
  SBD_ACTION_ICON_WIDTH,
  TIMELINE_DEFAULT_HOUR_WIDTH,
  TIMELINE_PADDING,
  TRACK_HEIGHT,
} from "../../core/layoutConstants";
import { hasIcon } from "./actionIcons";
import { SBDActionIcon } from "./SBDActionIcon";
import { getSBDActionStyle } from "./styles";
import { TrackGroupDraw } from "./TrackGroupDraw";
import { useDetailPanelData } from "../useDetailPanelData";
import { useChainHighlightHandler } from "@opstel/SBD/core/useChainHighlighting";
import { useActionQuery } from "./useActionQuery";
import { SBDActionContextMenuItems } from "./SBDActionContextMenuItems";
import { Action } from "@opstel/query/v2/models/action";
import classNames from "classnames";

export interface SBDActionProps {
  readonly actionId: string;
  readonly occupation: TrackOccupation;
  readonly hasError: boolean;
  readonly zoomLevel: number;
  readonly maxOrder: number;
}

export function SBDAction({ actionId, occupation, hasError, zoomLevel, maxOrder }: SBDActionProps) {
  const { highlightedShuntingUnitId, toggleHighlightedShuntingUnit } = useChainHighlightHandler();

  const isHighlighted = highlightedShuntingUnitId === occupation.shuntingUnit.guid;
  const handleToggleHighlightedShuntingUnit = useCallback(() => {
    toggleHighlightedShuntingUnit(occupation.shuntingUnit.guid);
  }, [toggleHighlightedShuntingUnit, occupation.shuntingUnit.guid]);

  return (
    <StatelessActionMemo
      actionId={actionId}
      occupation={occupation}
      hasError={hasError}
      zoomLevel={zoomLevel}
      maxOrder={maxOrder}
      isHighlighted={isHighlighted}
      toggleHighlightedShuntingUnit={handleToggleHighlightedShuntingUnit}
    />
  );
}

const StatelessActionMemo = memo(StatelessAction);

type StatelessActionProps = SBDActionProps & {
  readonly isHighlighted: boolean;
  readonly toggleHighlightedShuntingUnit: () => void;
};

function StatelessAction({
  actionId,
  occupation,
  hasError,
  zoomLevel,
  isHighlighted,
  maxOrder,
  toggleHighlightedShuntingUnit,
}: StatelessActionProps) {
  const { data: action, isPending, isError } = useActionQuery({ actionId });
  const { theme } = useTheme();
  const setDetailPanelData = useDetailPanelData((state) => state.setDetailPanelData);
  const scenarioStart = useSelectedTimeStore((state) => state.scenarioInterval?.start)!;

  if (isPending || isError) {
    return undefined;
  }

  const trainUnitKind = occupation.shuntingUnit.planTrainUnits.inAnyDirection()[0].kind;
  const style = getSBDActionStyle(trainUnitKind, theme);

  const drawStartTime = DateTime.max(action.startTime, occupation.startTime);
  const drawEndTime = DateTime.min(action.endTime, occupation.endTime);

  const drawStartTimeInHours = drawStartTime.diff(scenarioStart).as("hours");
  const drawDurationInHours = drawEndTime.diff(drawStartTime).as("hours");

  const width = drawDurationInHours * TIMELINE_DEFAULT_HOUR_WIDTH * zoomLevel;
  const left = drawStartTimeInHours * TIMELINE_DEFAULT_HOUR_WIDTH * zoomLevel;
  const top = getTrackOccupationPosition(occupation.order);

  const isFixed = action.changeability === "Fixed";
  const isActionForbiddenToChangeChangeability =
    action.type === "Wrapper" ||
    action.type === "WrapperSplit" ||
    action.type === "WrapperCombine" ||
    action.type === "Filler";

  const icons = iconsShown(width, action, occupation, style.color);
  const iconsContainerClassName = shouldShowIconsRight(action, occupation)
    ? "flex gap-1 items-center right-0 sticky ml-auto justify-end"
    : "flex gap-1 items-center left-0 sticky mr-auto justify-start";

  return (
    <>
      <ContextMenu>
        <Toggle.Root
          data-testid={`sbd-action-${actionId}`}
          className={classNames(
            "absolute z-10 flex border-0 bg-(--action-bg) px-1 text-(--action-color) outline-0",
            "hover:bg-(--hover-bg) hover:text-(--hover-color)",
            `focus:shadow-[0_0_0_2px_black]`,
            "data-[error=true]:shadow-none data-[error=true]:outline-2 data-[error=true]:outline-[--tractie-color-content-error] data-[error=true]:outline-solid",
            "data-[selected=true]:border-2 data-[selected=true]:border-solid data-[selected=true]:border-interactive-default-inverse data-[selected=true]:p-0.5",
            "data-[selected=true]:outline-2 data-[selected=true]:outline-purple-400 data-[selected=true]:outline-solid",
            "[&_svg]:h-4 [&_svg]:w-4 [&_svg]:fill-inherit [&_svg]:stroke-inherit [&_svg_path]:fill-[--action-color]",
            { "rounded-l": action.changeability !== "Fixed" },
          )}
          style={
            {
              height: `${TRACK_HEIGHT}px`,
              left: `${TIMELINE_PADDING + left}px`,
              top: `${top}px`,
              width: `${width}px`,
              "--action-bg": style?.backgroundColor,
              "--action-color": style?.color,
              "--hover-bg": style?.backgroundColorHover,
              "--hover-color": style?.colorHover,
            } as React.CSSProperties
          }
          data-error={hasError ? "true" : "false"}
          data-selected={isHighlighted}
          data-changeability={action.changeability}
          onPressedChange={toggleHighlightedShuntingUnit}
          onClick={() => setDetailPanelData(action.id, occupation.shuntingUnit)}
          aria-label={action.type}
          onContextMenu={(e: React.MouseEvent<HTMLButtonElement>) => {
            if (isFixed || isActionForbiddenToChangeChangeability) {
              e.preventDefault();
            }
          }}
        >
          <div className={iconsContainerClassName}>
            {hasError && (
              <tractie-icon name="alert-filled" size="xs" color="--tractie-color-content-error" />
            )}
            {shouldShowIconsRight(action, occupation) ? icons.toReversed() : icons}
          </div>
        </Toggle.Root>
        <SBDActionContextMenuItems
          actionId={actionId}
          currentChangeability={action.changeability}
        />
      </ContextMenu>
      <TrackGroupDraw
        maxOrder={maxOrder}
        order={occupation.order}
        trackGroupDrawDirection={occupation.trackGroupDrawDirection}
        width={width}
        left={left}
        $backgroundColor={style?.backgroundColor}
        $backgroundColorHover={style?.backgroundColorHover}
        $color={style?.color}
        $colorHover={style?.colorHover}
      />
    </>
  );
}

function shouldShowIconsRight(action: Action, occupation: TrackOccupation) {
  return (
    occupation.actions.at(-1)?.id === action.id &&
    action.type === "Move" &&
    !isPassingTrain(occupation)
  );
}

function maxNumberOfIconsShown(width: number) {
  return Math.floor(
    (width - 2 * SBD_ACTION_ICON_PADDING + SBD_ACTION_ICON_GAP) /
      (SBD_ACTION_ICON_WIDTH + SBD_ACTION_ICON_GAP),
  );
}

function iconsShown(
  width: number,
  action: Action,
  occupation: TrackOccupation,
  color: string,
): React.JSX.Element[] {
  if (!areIconsVisibleBasedOnTrackGroupAndActionIndex(occupation, action)) {
    return [];
  }
  const maxNumber = maxNumberOfIconsShown(width);
  const showActionIcon = hasIcon(action.type);

  const icons: React.JSX.Element[] = [];
  if (icons.length < maxNumber && showActionIcon) {
    icons.push(
      <SBDActionIcon key={"action-icon"} action={action} occupation={occupation} color={color} />,
    );
  }
  if (icons.length < maxNumber && action.changeability === "Pinned") {
    icons.push(<tractie-icon name="lock" size="xs" key={"lock-icon"} color={color} />);
  }
  return icons;
}

function areIconsVisibleBasedOnTrackGroupAndActionIndex(
  occupation: TrackOccupation,
  action: Action,
) {
  if (occupation.trackGroupDrawDirection === "None") {
    return true;
  }
  if (occupation.trackGroupDrawDirection === "Both") {
    return false;
  }
  if (occupation.trackGroupDrawDirection === "Down") {
    return occupation.actions.at(-1)?.id !== action.id;
  }
  return occupation.actions.at(-1)?.id === action.id;
}
